import CustomTable from "@/components/CustomTable";
import {
  generalSetting,
  getGeneralSetting,
  getMerchantTeams,
  paymentConfig,
} from "@/services/Settings.service";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { columnsT } from "./columns";
import { Button } from "@/components/ui/button";
import { IoFunnelOutline } from "react-icons/io5";
import { Input } from "@/components/ui/input";
import { ChevronLeft, UserPlus } from "lucide-react";
import Invite from "./common/Invite";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link, useNavigate } from "react-router";
import { HiOutlineSlash } from "react-icons/hi2";
import CustomPageHeader from "../common/CustomPageHeader";
import CustomTabs from "@/components/CustomTabs";
import Profile from "./Profile";
import TeamManagement from "./TeamManagement";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useAuthState } from "@/context";
import { CustomModal } from "@/components/custom-modal";
import { InputOTP, InputOTPSlot } from "@/components/ui/input-otp";
import { Storage } from "@/services/storage-services";
import { ToastMessage, type } from "@/lib/toastr";

const ApiManagement = () => {
  const [filter, setFilter] = useState({
    page: 1,
    search: "",
  });
  const { data, isLoading, isFetching, refetch, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["teams"],
      queryFn: async ({ pageParam = filter.page }) =>
        await getMerchantTeams(pageParam, filter),
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length === 0) return undefined;
        return pages.length + 1;
      },
      placeholderData: keepPreviousData,
    });

  const dataArr = data?.pages?.map((page) => page.data.data.data).flat();
  const meta = data?.pages?.map((page) => page.data.data.meta).flat()[0];

  const handlePageChange = (page) => {
    setFilter({ ...filter, page: page });
  };

  return (
    <div className="flex flex-col gap-8 p-6">
      <div>
        <Breadcrumb>
          <BreadcrumbList className="gap-2">
            <BreadcrumbItem>
              <Link
                to="/dashboard/settings"
                className="flex items-center gap-1 text-sm text-primary0"
              >
                <ChevronLeft className="w-3 h-3" />
                Settings
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <HiOutlineSlash />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink className="flex items-center gap-1 text-sm text-gray-60">
                API Management
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <CustomPageHeader
        title="API Management"
        description="Update essential business details and company information"
      />
      <div className="container mx-auto">
        <CustomTabs
          pos="flex flex-row "
          tabs={["Payment Config", "API Version", "API configuration"]}
          tabData={{
            "Payment Config": <PaymentConfig />,
            "API Version": <AppVersion />,
            "API configuration": <p>API configuration</p>,
          }}
        />
      </div>
    </div>
  );
};

export default ApiManagement;

const PaymentConfig = () => {
  const [twoFa, setTwoFa] = useState(false);
  const [twoFactorAuthenticationCode, setTwoFactorAuthenticationCode] =
    useState("");
  const navigate = useNavigate();
  const [publicKey, setPublicKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const { state } = useAuthState();
  const mutation = useMutation({
    mutationFn: (formData) => paymentConfig(formData),
    mutationKey: ["payConfig"],
    onSuccess: async (data) => {
      if (data?.data?.statusCode === 200) {
        setTwoFa(false);
        ToastMessage(type.SUCCESS, "Settings saved successfully");
      }
    },
    onError: (err) => {
      setTwoFa(false);
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const handleSave = () => {
    if (Storage.getItem("twoFa")) {
      setTwoFa(true);
    } else {
      navigate("/dashboard/settings/general");
    }
  };

  const handleSubmit2Fa = async () => {
    await mutation.mutate({
      publicKey,
      secretKey,
      twoFactorAuthenticationCode,
    });
  };
  return (
    <>
      <div className="grid bg-off-white-1 p-6 rounded-[12px] gap-4 w-[541px]">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-primary-3">Payment Config</h3>
        </div>
        <div className="grid gap-5">
          <div
            className={cn(
              "flex flex-col w-full items-start gap-1 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
            )}
          >
            <Label className="text-xs">Public Key</Label>

            <Input
              type="text"
              className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
              onChange={(e) => setPublicKey(e?.target?.value)}
              value={publicKey}
              placeholder="Enter Public Key"
            />
          </div>

          <div
            className={cn(
              "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
            )}
          >
            <Label className="text-xs">Secret Key</Label>

            <Input
              type="text"
              className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
              onChange={(e) => setSecretKey(e?.target?.value)}
              value={secretKey}
              placeholder="Enter Secret Key"
            />
          </div>
        </div>

        <Button
          className=" bg-transparent text-primary0 border-[1px] border-primary0 hover:text-primary0"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
      <ConfirmOTP
        setTwoFa={setTwoFa}
        setTwoFactorAuthenticationCode={setTwoFactorAuthenticationCode}
        twoFa={twoFa}
        handleSubmit={handleSubmit2Fa}
      />
    </>
  );
};

const ConfirmOTP = ({
  setTwoFactorAuthenticationCode,
  setTwoFa,
  twoFa,
  handleSubmit,
}) => (
  <CustomModal open={twoFa} size="md">
    <div className="flex flex-col gap-4 p-8">
      <div>
        <p>Enter 2FA Code</p>
        <p>Please kindly enter the 6-digit code below</p>
      </div>
      <InputOTP
        maxLength={6}
        onChange={(e) => setTwoFactorAuthenticationCode(e)}
        className="flex justify-between gap-8"
      >
        <InputOTPSlot index={0} className=" w-14 h-14" />
        <InputOTPSlot index={1} className=" w-14 h-14" />
        <InputOTPSlot index={2} className="w-14 h-14" />
        <InputOTPSlot index={3} className=" w-14 h-14" />
        <InputOTPSlot index={4} className=" w-14 h-14" />
        <InputOTPSlot index={5} className="w-14 h-14" />
      </InputOTP>
      <Button onClick={handleSubmit}>Submit</Button>
    </div>
  </CustomModal>
);

const AppVersion = () => {
  const [minimumAppVersion, setMinimumAppVersion] = useState(Number);

  const { data, isFetching } = useQuery({
    queryKey: ["getGeneralSettings"],
    queryFn: async () => await getGeneralSetting(),
    refetchOnMount: "always",
  });

  useEffect(() => {
    setMinimumAppVersion(data?.data?.data?.api?.minimumAppVersion);
  }, [data?.data?.data?.api?.minimumAppVersion]);

  const mutation = useMutation({
    mutationFn: (formData) => generalSetting(formData),
    mutationKey: ["appVersionSetting"],
    onSuccess: async (data) => {
      if (data?.data?.statusCode === 200) navigate("settings");
      ToastMessage(type.SUCCESS, "Settings saved successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const onSubmit = async () => {
    mutation.mutate({ minimumAppVersion });
  };

  return (
    <>
      <div className="grid bg-off-white-1 p-6 rounded-[12px] gap-4 w-[541px]">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-primary-3">Set minimum app version</h3>
          <p className="text-sm text-gray-80">
            This action will require users to upgrade their current version
          </p>
        </div>
        <div className="grid gap-5">
          <div
            className={cn(
              "flex flex-col w-full items-start gap-1 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
            )}
          >
            <Label className="text-xs">Minimum app version</Label>

            <Input
              type="text"
              className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
              onChange={(e) => setMinimumAppVersion(e?.target?.value)}
              value={minimumAppVersion}
              placeholder="Enter "
            />
          </div>
        </div>

        <Button
          className=" bg-transparent text-primary0 border-[1px] border-primary0 hover:text-primary0"
          onClick={onSubmit}
        >
          Save
        </Button>
      </div>
    </>
  );
};
